import { useContext, useEffect, useRef, useState } from 'react'

import { ConditionGroupType, PatternTreeContext } from '../contexts/PatternTreeContext'
import { PreviewConflictContext } from './PreviewConflictContext'
import { PreviewResult, solvePattern } from './solve/solveModel'

const DEBOUNCE_MS = 150

export interface PreviewState {
  result: PreviewResult
  /**
   * The exact groups array the result was solved from. Consumers deriving
   * per-condition data (labels) key memos on THIS, not the live context
   * groups — the live reference changes on every keystroke, which would
   * recompute per keystroke and defeat React.memo on the chart.
   */
  solvedGroups: ConditionGroupType[]
}

/**
 * Live preview computation: gated on the active side's group-array REFERENCE
 * (setPattern replaces it on every mutation) plus a short debounce, so typing
 * in unrelated fields never re-solves and rapid edits coalesce. Publishes
 * proven conflicts into PreviewConflictContext for the amber card outlines.
 */
export const usePreviewResult = (enabled = true): PreviewState | null => {
  const { pattern, activeTab } = useContext(PatternTreeContext)
  const { setConflictConditionIds } = useContext(PreviewConflictContext)
  const [state, setState] = useState<PreviewState | null>(null)
  const lastGroupsRef = useRef<unknown>(null)

  const groups = activeTab === 'sell' ? pattern?.sellConditionGroups : pattern?.buyConditionGroups
  const patternId = pattern?.id ?? 'unsaved'

  useEffect(() => {
    // MUI Collapse keeps children mounted, so a collapsed panel would still
    // pay every solve — gate on enabled and recompute on expand instead.
    // Clear published conflicts while collapsed (edits made then are never
    // re-solved, so amber outlines could go stale) and force a fresh solve
    // on expand.
    if (!enabled) {
      lastGroupsRef.current = null
      setConflictConditionIds([])
      return undefined
    }
    if (!groups) {
      setState(null)
      setConflictConditionIds([])
      return undefined
    }
    if (groups === lastGroupsRef.current) return undefined
    const timer = setTimeout(() => {
      lastGroupsRef.current = groups
      const solved = solvePattern(groups, patternId)
      setState({ result: solved, solvedGroups: groups })
      setConflictConditionIds(solved.conflictConditionIds)
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [enabled, groups, patternId, setConflictConditionIds])

  return state
}
